var _dec, _dec2, _class, _RangeInput;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { warn } from '@instructure/console';
import { ContextView } from '@instructure/ui-view';
import { FormField } from '@instructure/ui-form-field';
import { addEventListener } from '@instructure/ui-dom-utils';
import { withStyle } from '@instructure/emotion';
import { omitProps, pickProps, withDeterministicId } from '@instructure/ui-react-utils';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let RangeInput = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_RangeInput = class RangeInput extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._input = null;
    this._inputListener = null;
    this._changeListener = null;
    this.defaultId = void 0;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleInputRef = el => {
      this._input = el;
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(el);
      }
    };
    this.handleChange = event => {
      const _this$props = this.props,
        onChange = _this$props.onChange,
        value = _this$props.value;
      if (typeof value === 'undefined') {
        this.setState({
          value: event.target.value
        });
      }
      if (typeof onChange === 'function') {
        onChange(event.target.value);
      }
    };
    // controlled input must have an onChange, but we're handling it with native events
    this.noopChange = () => {};
    if (typeof props.value === 'undefined') {
      this.state = {
        value: props.defaultValue
      };
    }
    this.defaultId = props.deterministicId();
  }

  /* workaround for https://github.com/facebook/react/issues/554 */
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
    if (this._input !== null) {
      // https://connect.microsoft.com/IE/Feedback/Details/856998
      this._inputListener = addEventListener(this._input, 'input', this.handleChange);
      this._changeListener = addEventListener(this._input, 'change', this.handleChange);
    }
  }
  componentWillUnmount() {
    var _this$_inputListener, _this$_changeListener;
    if (!this._input) {
      return;
    }
    (_this$_inputListener = this._inputListener) === null || _this$_inputListener === void 0 ? void 0 : _this$_inputListener.remove();
    (_this$_changeListener = this._changeListener) === null || _this$_changeListener === void 0 ? void 0 : _this$_changeListener.remove();
  }
  /* end workaround */

  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
  }
  get value() {
    const value = typeof this.props.value === 'undefined' ? this.state.value : this.props.value;
    return typeof value === 'string' ? parseInt(value) : value;
  }
  get id() {
    return this.props.id || this.defaultId;
  }
  renderValue() {
    if (this.props.displayValue) {
      var _this$props$styles;
      if (!this.value) {
        warn(false, 'RangeInput should have a `value` or `defaultValue` set for the value to be displayed. If no value has to be displayed, set `displayValue={false}`.');
        return null;
      }
      const props = {
        [RangeInput.outputLocatorAttribute]: this.id
      };
      return _jsx(ContextView, {
        background: "inverse",
        placement: "end center",
        children: _jsx("div", {
          ...props,
          css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.rangeInputInputValue,
          children: this.props.formatValue(this.value, this.props.max)
        })
      });
    }
    return null;
  }
  render() {
    var _this$props$styles2, _this$props$styles3;
    const _this$props4 = this.props,
      formatValue = _this$props4.formatValue,
      disabled = _this$props4.disabled,
      readOnly = _this$props4.readOnly;
    const props = omitProps(this.props, RangeInput.allowedProps);
    return _jsx(FormField, {
      ...pickProps(this.props, FormField.allowedProps),
      label: this.props.label,
      id: this.id,
      elementRef: this.handleRef,
      "data-cid": "RangeInput",
      children: _jsxs("div", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.rangeInput,
        children: [_jsx("input", {
          css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.rangeInputInput,
          ref: this.handleInputRef,
          type: "range",
          id: this.id,
          min: this.props.min,
          max: this.props.max,
          step: this.props.step,
          value: this.value,
          onChange: this.noopChange,
          "aria-valuetext": formatValue(this.value, this.props.max),
          ...props,
          disabled: disabled || readOnly,
          "aria-disabled": disabled || readOnly ? 'true' : void 0
        }), this.renderValue()]
      })
    });
  }
}, _RangeInput.displayName = "RangeInput", _RangeInput.componentId = 'RangeInput', _RangeInput.outputLocatorAttribute = 'data-range-output', _RangeInput.allowedProps = allowedProps, _RangeInput.defaultProps = {
  step: 1,
  formatValue: val => val,
  // If min and max has default value, they don't give a warning if not set, even if they are required props.
  // TODO: figure out if they don't need to be required or remove defaults in V9.
  max: 0,
  min: 0,
  inline: false,
  size: 'medium',
  layout: 'stacked',
  displayValue: true,
  disabled: false,
  readOnly: false,
  thumbVariant: 'deprecated'
}, _RangeInput)) || _class) || _class);
export default RangeInput;
export { RangeInput };